import { useEffect, useRef, useState } from "react";
import { ArrowRight, PackageOpen, RefreshCw } from "lucide-react";
import { api, ApiError } from "../api";
import { Alert, formatNumber, PageHeader, Loading, EmptyState } from "../components/Ui";
import type { Balance, Warehouse, MoveInventoryRequest } from "../types";
import { readOperation, saveOperation } from "../operationJournal";
import { unitLabels } from "../types";

import { updateRouteParams } from "../navigation";
import { OneCInboundPage } from "./OneCInboundPage";
import { routeId, usePageParams } from "./warehouseLocation";

export function PlacementPage({ canManage, actor }: { canManage: boolean; actor: string }) {
  const params = usePageParams("placement");
  if (params.get("mode") === "manual") return <><button className="back-link" onClick={() => updateRouteParams({ mode: null })}>Размещение по документам 1С</button><ManualPlacementPage actor={actor} /></>;
  return <><OneCInboundPage canManage={canManage} actor={actor} placement /><button className="button" onClick={() => updateRouteParams({ mode: "manual" })}>Другие перемещения</button></>;
}

function ManualPlacementPage({ actor }: { actor: string }) {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const params = usePageParams("placement");
  const requestedWarehouse = routeId(params.get("warehouse"));
  const activeWarehouse = requestedWarehouse ?? warehouses[0]?.id ?? "";
  const search = params.get("q") ?? "";
  const productId = routeId(params.get("product"));
  const locationId = routeId(params.get("location"));
  
  const [receivingBalances, setReceivingBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadedWarehouse, setLoadedWarehouse] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [sourceCode, setSourceCode] = useState("");
  const [targetCode, setTargetCode] = useState("");
  const [productCode, setProductCode] = useState("");
  const [quantity, setQuantity] = useState("");
  const [moving, setMoving] = useState(false);
  const journalKey = `wms.manual-move.pending:${actor}`;
  const [pending, setPending] = useState<MoveInventoryRequest | null>(null);
  const [journalBlocked, setJournalBlocked] = useState(false);
  useEffect(() => {
    let disposed = false;
    try {
      const saved = readOperation<MoveInventoryRequest>(sessionStorage, journalKey);
      setPending(saved);
      if (saved) {
        setSourceCode(saved.sourceLocationBarcode); setTargetCode(saved.targetLocationBarcode);
        setProductCode(saved.productCode); setQuantity(String(saved.quantity));
        void api.inventoryMoveStatus(saved.operationId).then(() => {
          if (disposed) return;
          sessionStorage.removeItem(journalKey); setPending(null);
          setSuccess("Перемещение уже записано. После F5 повторного движения нет."); void loadReceiving(true);
        }).catch(e => { if (!disposed && !(e instanceof ApiError && e.status === 404)) setError(e instanceof Error ? e.message : "Не удалось проверить перемещение"); });
      }
    } catch (e) { setJournalBlocked(true); setError(e instanceof Error ? e.message : "Журнал операции недоступен"); }
    return () => { disposed = true; };
  }, [journalKey]);

  const requestVersion = useRef(0);
  const movingRef = useRef(moving); movingRef.current = moving;
  useEffect(() => {
    let stopped = false;
    api.warehouses().then(whs => { if (!stopped) { setWarehouses(whs); if (!whs.length) setLoading(false); } })
      .catch(e => { if (!stopped) { setError(e instanceof ApiError ? e.message : "Не удалось загрузить склады"); setLoading(false); } });
    return () => { stopped = true; };
  }, []);
  useEffect(() => {
    if (!requestedWarehouse && activeWarehouse) updateRouteParams({ warehouse: activeWarehouse }, true);
  }, [requestedWarehouse, activeWarehouse]);
  useEffect(() => {
    if (!activeWarehouse) return;
    void loadReceiving();
    const timer = window.setInterval(() => { if (!movingRef.current) void loadReceiving(true); }, 10000);
    return () => { ++requestVersion.current; window.clearInterval(timer); };
  }, [activeWarehouse]);
  async function loadReceiving(background = false) {
    if (!activeWarehouse) return;
    const request = ++requestVersion.current;
    if (!background) setLoading(true);
    try {
      const allBalances = await api.balances(activeWarehouse);
      if (request !== requestVersion.current) return;
      setReceivingBalances(allBalances.filter((b: Balance) => b.locationCode === "RECEIVING"));
      setLoadedWarehouse(activeWarehouse); setError("");
    } catch (e) {
      if (request === requestVersion.current) setError(e instanceof ApiError ? e.message : "Не удалось загрузить остатки");
    } finally { if (request === requestVersion.current) setLoading(false); }
  }
  const balances = loadedWarehouse === activeWarehouse ? receivingBalances : [];
  const selected = balances.find(b => b.productId === productId && b.locationId === locationId);
  const term = search.trim().toLocaleLowerCase();
  const visibleBalances = balances.filter(b => b.availableQuantity > 0 &&
    (!term || [b.productCode, b.productName, b.locationCode].some(value => value.toLocaleLowerCase().includes(term))));
  useEffect(() => {
    if (sessionStorage.getItem(journalKey)) return;
    setSourceCode(selected?.locationBarcode ?? ""); setProductCode(selected?.productCode ?? "");
    setTargetCode(""); setQuantity(""); setSuccess("");
  }, [activeWarehouse, productId, locationId, selected?.productCode, selected?.locationBarcode]);

  async function handleMove(e: React.FormEvent) {
    e.preventDefault();
    if (movingRef.current || journalBlocked) return;
    movingRef.current = true;
    setError("");
    setSuccess("");
    setMoving(true);
    
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      setError("Укажите корректное количество");
      setMoving(false);
      movingRef.current = false;
      return;
    }

    try {
      const request = saveOperation<MoveInventoryRequest>(sessionStorage, journalKey, {
        operationId: crypto.randomUUID(),
        sourceLocationBarcode: sourceCode.trim(),
        targetLocationBarcode: targetCode.trim(),
        productCode: productCode.trim(),
        quantity: qty
      });
      setPending(request);
      const result = await api.inventoryMove(request);
      sessionStorage.removeItem(journalKey); setPending(null);
      setSuccess(result.alreadyProcessed ? "Перемещение уже было записано. Повторного движения нет." : "Товар успешно перемещён");
      setQuantity("");
      void loadReceiving();
    } catch (e) {
      if (e instanceof ApiError && [400, 404, 409, 422].includes(e.status) && e.title !== "Operation ID conflict") {
        sessionStorage.removeItem(journalKey); setPending(null);
      }
      setError(e instanceof ApiError ? e.message : "Ошибка при перемещении");
    } finally {
      setMoving(false);
      movingRef.current = false;
    }
  }

  function handlePrefill(balance: Balance) {
    updateRouteParams({ product: balance.productId, location: balance.locationId });
    document.getElementById("targetCodeInput")?.focus();
  }

  return <>
    <PageHeader 
      eyebrow="Складские операции" 
      title="Размещение товаров" 
      text="Перемещение товаров из зоны приемки на места постоянного хранения." 
      action={
        <div style={{ display: "flex", gap: "1rem" }}>
          {warehouses.length > 0 && (
            <select aria-label="Склад" className="field" value={activeWarehouse} onChange={e => updateRouteParams({ warehouse: e.target.value, product: null, location: null })}>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <button className="button button--secondary" onClick={() => void loadReceiving()}><RefreshCw size={17} />Обновить</button>
        </div>
      }
    />

    {error && <Alert>{error}</Alert>}
    {success && <div className="status status--picked" style={{ padding: "1rem", borderRadius: "8px", marginBottom: "1rem" }}>{success}</div>}

    <section className="panel"><label className="field"><span>Поиск</span><input placeholder="Код или название товара" value={search} onChange={e => updateRouteParams({ q: e.target.value || null }, true)} /></label></section>
    <div className="placement-grid">
      
      {/* Left Column: Receiving Zone */}
      <section className="panel">
        <h3 style={{ marginBottom: "1rem" }}>Товары в зоне приемки</h3>
        <p style={{ color: "var(--color-text-dim)", marginBottom: "1.5rem" }}>
          Здесь отображаются товары, полученные от 1С, но еще не распределенные по ячейкам.
        </p>

        {loading ? <Loading /> : visibleBalances.length === 0 ? (
          <EmptyState icon={<PackageOpen />} title="Товары не найдены" text="Проверьте поиск и выбранный склад. В зоне приёмки может не быть товаров для размещения." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Код / Товар</th>
                  <th style={{ textAlign: "right" }}>Кол-во</th>
                  <th style={{ width: "1%" }}></th>
                </tr>
              </thead>
              <tbody>
                {visibleBalances.map(b => (
                  <tr key={`${b.locationId}-${b.productId}`}>
                    <td>
                      <div><b>{b.productName}</b></div>
                      <small className="mono">{b.productCode}</small>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <b>{formatNumber(b.availableQuantity)}</b> {unitLabels[b.unit] ?? b.unit}
                    </td>
                    <td>
                      <button className="button button--secondary" aria-label={`Разместить ${b.productName}`} onClick={() => handlePrefill(b)}>
                        <ArrowRight size={17} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Right Column: Move Form */}
      <section className="panel">
        {productId && <button className="back-link" onClick={() => updateRouteParams({ product: null, location: null })}>Назад к товарам</button>}
        <h3 style={{ marginBottom: "1.5rem" }}>{selected ? selected.productName : productId ? "Карточка размещения" : "Ручное перемещение"}</h3>
        {pending && <Alert>Результат перемещения ещё не подтверждён. Повтор будет отправлен с тем же номером и количеством.</Alert>}
        {productId && (loading && !selected ? <Loading /> : !selected ? <Alert>Товар больше не находится в зоне приёмки выбранного склада или недоступен.</Alert> : <p>{selected.productCode} · {selected.locationCode} · Доступно: {formatNumber(selected.availableQuantity)} {unitLabels[selected.unit] ?? selected.unit}</p>)}
        <form onSubmit={handleMove} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          <fieldset disabled={moving || Boolean(pending) || journalBlocked} style={{ display: "contents" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label>Штрихкод ячейки ОТКУДА</label>
              <input required readOnly={Boolean(productId)} aria-label="Штрихкод ячейки ОТКУДА" className="field" value={sourceCode} onChange={e => setSourceCode(e.target.value)} placeholder="REC-..." />
            </div>
            <div>
              <label>Штрихкод ячейки КУДА</label>
              <input required id="targetCodeInput" className="field" value={targetCode} onChange={e => setTargetCode(e.target.value)} placeholder="Например: A-01-01" />
            </div>
          </div>

          <div>
            <label>Артикул товара</label>
            <input required readOnly={Boolean(productId)} aria-label="Артикул товара" className="field" value={productCode} onChange={e => setProductCode(e.target.value)} />
          </div>

          <div>
            <label>Количество</label>
            <input required type="number" step="0.001" min="0.001" className="field" value={quantity} onChange={e => setQuantity(e.target.value)} />
          </div>

          </fieldset>
          <button type="submit" className="button button--primary" disabled={moving || journalBlocked || (!pending && Boolean(productId && (!selected || selected.availableQuantity <= 0)))}>
            {moving ? "Перемещение..." : pending ? "Проверить и повторить запрос" : "Переместить товар"}
          </button>
        </form>
      </section>

    </div>
  </>;
}
